// Teeth Drop (docs/04 §17.4): eyeballs dropped through a board of teeth pegs. The player picks the
// drop slot; the bounces are a seeded Galton walk (deterministic, replay-safe). The sim returns
// every ball's path so the view only replays it.
import { B } from '../../content/balance';
import { D, type Decimal } from '../decimal';
import type { Modifiers } from '../modifiers';
import type { Rng } from '../rng';
import type { PlinkoBonus, PlinkoDrop } from '../types';

export const PLINKO_SLOTS = B.PLINKO_BUCKETS.length;
const MAX_POS = PLINKO_SLOTS * 2;

export function createPlinko(rng: Rng, par: Decimal, mods: Modifiers, plug: boolean): PlinkoBonus {
  // A Plug part hides in one bucket (never the centre) when the good path is open.
  let plugBucket = -1;
  if (plug) {
    const choices = [0, 2, 3, 5, 6, 8];
    plugBucket = rng.pick(choices);
  }
  return {
    mode: 'plinko',
    par,
    balls: Math.max(1, mods.plinkoBalls),
    drops: [],
    golden: B.PLINKO_GOLDEN.slice(0, mods.goldenPegs) as [number, number][],
    goldenX: mods.goldenPegX,
    plugBucket,
    won: 0,
    chomps: 0,
    done: false,
    payout: D(0),
    voided: false,
  };
}

export function plinkoDrop(b: PlinkoBonus, slot: number, rng: Rng): PlinkoDrop | null {
  if (b.done || b.balls <= 0) return null;
  const start = Math.max(0, Math.min(PLINKO_SLOTS - 1, Math.round(slot)));
  let pos = start * 2 + 1;
  const path: number[] = [];
  let golden = false;
  for (let row = 0; row < B.PLINKO_ROWS; row++) {
    pos += rng.chance(0.5) ? 1 : -1;
    // walls bounce back
    if (pos < 0) pos = 1;
    if (pos > MAX_POS) pos = MAX_POS - 1;
    path.push(pos);
    if (b.golden.some(([r, p]) => r === row && p === pos)) golden = true;
  }
  const bucket = Math.max(0, Math.min(PLINKO_SLOTS - 1, Math.floor(pos / 2)));
  const raw = b.plugBucket === bucket ? 'plug' : B.PLINKO_BUCKETS[bucket]!;
  if (raw === 'chomp') b.chomps++;
  else if (raw !== 'plug') b.won += Number(raw) * (golden ? b.goldenX : 1);
  const drop: PlinkoDrop = { start, path, bucket, prize: raw, golden };
  b.drops.push(drop);
  b.balls--;
  if (b.balls <= 0) {
    b.done = true;
    b.payout = b.par.mul(b.won).floor();
  }
  return drop;
}

/** Average skill: aim at the middle. */
export function plinkoAuto(b: PlinkoBonus, rng: Rng): void {
  while (!b.done) plinkoDrop(b, Math.floor(PLINKO_SLOTS / 2), rng);
}
